import { useEffect, useState } from "react";
import { api, formatApiError, mediaUrl } from "../api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import ImagePicker from "../components/ImagePicker";

const empty = { image_url: "", title: "", subtitle: "", sort_order: 0 };

export default function HeroSlidesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/api/admin/hero-slides");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setForm(empty);
    setOpen(true);
  };
  const startEdit = (it) => { setForm(it); setOpen(true); };

  const remove = async (id) => {
    if (!window.confirm("Delete this slide?")) return;
    try {
      await api.delete(`/api/admin/hero-slides/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!form.image_url) { toast.error("Image is required"); return; }
    setSaving(true);
    try {
      const payload = {
        image_url: form.image_url,
        title: form.title || "",
        subtitle: form.subtitle || "",
        sort_order: form.id ? Number(form.sort_order) || 0 : items.length,
      };
      if (form.id) {
        const { data } = await api.put(`/api/admin/hero-slides/${form.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)).sort((a, b) => a.sort_order - b.sort_order));
      } else {
        const { data } = await api.post("/api/admin/hero-slides", payload);
        setItems((arr) => [...arr, data].sort((a, b) => a.sort_order - b.sort_order));
      }
      setOpen(false);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const reorder = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const a = items[idx];
    const b = items[newIdx];
    try {
      await Promise.all([
        api.put(`/api/admin/hero-slides/${a.id}`, { ...a, sort_order: b.sort_order }),
        api.put(`/api/admin/hero-slides/${b.id}`, { ...b, sort_order: a.sort_order }),
      ]);
      const next = [...items];
      next[idx] = { ...b, sort_order: a.sort_order };
      next[newIdx] = { ...a, sort_order: b.sort_order };
      next.sort((x, y) => x.sort_order - y.sort_order);
      setItems(next);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="hero-slides-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-serif font-bold">Hero Slides</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage the rotating image carousel at the top of the homepage. Up to ~7 slides recommended.
          </p>
        </div>
        <Button onClick={startNew} className="bg-primary hover:bg-primary-dark text-white" data-testid="new-slide-btn">
          <Plus className="w-4 h-4 mr-2" /> Add slide
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">No slides yet. Add at least one.</Card>
      ) : (
        <div className="space-y-3">
          {items.map((it, idx) => (
            <Card key={it.id} className="p-4 flex items-center gap-4" data-testid={`slide-row-${it.id}`}>
              <div className="text-xs font-mono text-muted-foreground w-6">#{idx + 1}</div>
              <img
                src={mediaUrl(it.image_url)}
                alt={it.title}
                className="w-32 h-20 object-cover rounded-md bg-muted shrink-0"
                onError={(e) => (e.target.style.opacity = 0.3)}
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{it.title || <span className="text-muted-foreground italic">Untitled</span>}</div>
                <div className="text-xs text-muted-foreground truncate">{it.subtitle}</div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => reorder(idx, -1)} disabled={idx === 0} title="Move up">
                  <ArrowUp className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => reorder(idx, 1)} disabled={idx === items.length - 1} title="Move down">
                  <ArrowDown className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => startEdit(it)} data-testid={`edit-slide-${it.id}`}>
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="outline" className="text-destructive" onClick={() => remove(it.id)} data-testid={`delete-slide-${it.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{form.id ? "Edit slide" : "Add slide"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <ImagePicker
              label="Slide image *"
              value={form.image_url}
              onChange={(v) => setForm({ ...form, image_url: v })}
              testId="slide-image"
            />
            <div>
              <Label>Title (shown over image)</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. United in Heritage"
                data-testid="slide-title-input"
              />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Textarea
                rows={2}
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                placeholder="A short tagline shown under the title"
              />
            </div>
            <div>
              <Label>Sort order</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">Lower numbers appear first.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-primary hover:bg-primary-dark text-white"
              data-testid="save-slide-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
